'use client';

import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { observer } from 'mobx-react-lite';

import { Matrix } from 'components/Matrix/Matrix';
import { GetServerSideFavoritesPropsReturnType } from 'services/favoritos/fetchFavorites';

import { browserLogger } from 'utils/logger/browserLogger';
import { getFullErrorObject } from 'utils/getErrorMessage';
import favoriteClient from 'services/favoritos/favoriteClient';
import authStore from 'stores/AuthStore';
import { Loading } from 'components/Loading';
import locationStore from 'stores/LocationStore';

const defaultSpecialUUIDs = [
  'para-voce-tag-similarity',
  'para-voce-user-preferences',
  'para-voce-user-recommender',
  'para-voce-recents-similarity',
];

type Props = GetServerSideFavoritesPropsReturnType & {
  isAuthenticated: boolean;
};

function FavoriteContentTemplate({
  isAuthenticated,
  hydrationData: {
    favoritesHydration,
    locationHydration,
  },
}: Props) {
  const { ref, inView } = useInView();
  const [components, setComponents] = useState(favoritesHydration.favoritesResult.components || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(Math.ceil(favoritesHydration.favoritesResult.components_total / 4));
  const [isLoading, setIsLoading] = useState(false);
  const [specialUUIDs, setSpecialUUIDs] = useState(defaultSpecialUUIDs);

  const loadFavorites = async (page: number, includeResponse = true) => {
    const logger = browserLogger();
    logger.init();

    setIsLoading(true);

    try {
      const result = await favoriteClient({
        include_response: includeResponse,
        afterComponentsCount: 4,
        components_limit: 4,
        components_page: page,
        user_id: authStore.userData?.dataUser?.id,
        events_ids: favoritesHydration.cardPreviousView.recents?.join(',') || '',
      });

      if ('error' in result) throw result?.error;

      if (result && result.components?.length > 0) {
        if (!includeResponse) {
          setSpecialUUIDs((prevUUIDs) => {
            const newUUIDs = result.components.map((item) => item.uuid);
            const uniqueUUIDs = new Set(prevUUIDs);
            newUUIDs.forEach((uuid) => uniqueUUIDs.add(uuid));
            return Array.from(uniqueUUIDs);
          });
        }

        setComponents((prevComponents) => (page === 1
          ? result.components
          : [...prevComponents, ...result.components]));
        setCurrentPage(page);
        setTotalPages(Math.ceil(result.components_total / 4));
      }
    } catch (error) {
      const err = getFullErrorObject<Error>(error) as Error;
      logger.error({
        message: 'Uncaught error: loading elements from the favorites page',
        data: err.message || 'loadMoreFavorites error',
        extra: {
          error,
        },
      }, 'fn loadMoreFavorites()');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (inView && currentPage < totalPages && !isLoading && authStore.isAuthenticated) {
      loadFavorites(currentPage + 1);
    }
  }, [inView]);

  useEffect(() => {
    if (!(isAuthenticated === authStore.isAuthenticated) || locationStore.selectedLocation.city !== locationHydration.selectedLocation.city) {
      setComponents([]);
      setCurrentPage(0);
      loadFavorites(1, false);
    }
  }, [authStore.isAuthenticated, locationStore.selectedLocation.city]);

  return (
    <>
      <Matrix
        components={components}
        matrixType={favoritesHydration.favoritesResult?.template}
        matrixUuid={favoritesHydration.favoritesResult?.uuid}
        cardPreviousView={favoritesHydration.cardPreviousView}
        userLocation={locationHydration.selectedLocation}
        specialUUIDs={specialUUIDs}
      />

      {isLoading && <Loading isNotFullScreen />}

      <div ref={ref} />
    </>
  );
}

export default observer(FavoriteContentTemplate);
